import { Link } from "react-router-dom";
import { Target, Eye, ListChecks, ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";

const ABOUT_IMAGE =
    "https://images.unsplash.com/photo-1676275774895-7fbba85dce94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxpbnRlcmFjdGl2ZSUyMHRvdWNoJTIwZGlzcGxheSUyMHBhbmVsJTIwbWVldGluZyUyMHJvb218ZW58MHx8fHwxNzkxODA4NzYwfDA&ixlib=rb-4.1.0&q=85";

const GOALS = [
    "Ship AI and ERP solutions that remove hours of manual work every single week.",
    "Put interactive panels in classrooms and boardrooms across every region we serve.",
    "Deliver reliable solar systems that cut energy costs and carbon footprints alike.",
    "Back every deployment with training and support that keeps teams confident and self-sufficient.",
    "Grow long-term partnerships measured by real results, never one-time sales.",
];

const About = () => (
    <>
        <Seo
            title="About Us | Coaching Crafters AI"
            description="Coaching Crafters AI is a technology and solutions company crafting AI web software, ERP platforms, interactive panels and solar systems — achieving all goals."
            path="/about"
        />

        {/* HERO */}
        <section className="relative overflow-hidden" data-testid="about-hero">
            <div
                className="pointer-events-none absolute -left-40 -top-40 h-[30rem] w-[30rem] rounded-full bg-ember/10 blur-3xl"
                aria-hidden="true"
            />
            <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-28">
                <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
                    <div>
                        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-ember">
                            About Us
                        </p>
                        <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                            <MaskedLine delay={0.1}>Technology,</MaskedLine>
                            <MaskedLine delay={0.25}>
                                <em className="font-medium italic text-ember">crafted</em> around you.
                            </MaskedLine>
                        </h1>
                        <Reveal delay={0.35}>
                            <p className="mt-7 text-base leading-relaxed text-ink/65 sm:text-lg" data-testid="about-intro-p1">
                                Coaching Crafters AI is a technology and solutions company built on one belief: the
                                right tools, in the right hands, turn ambition into achievement. We design, build and
                                deliver AI-powered web software, enterprise ERP platforms, interactive panels and
                                solar energy systems that help schools, businesses and institutions work smarter,
                                teach better and grow sustainably.
                            </p>
                            <p className="mt-5 text-base leading-relaxed text-ink/65 sm:text-lg" data-testid="about-intro-entity">
                                Coaching Crafters AI is the technology brand of Sarvacharya Education Pvt Ltd, an
                                education-focused company.
                            </p>
                            <p className="mt-5 text-base leading-relaxed text-ink/65 sm:text-lg" data-testid="about-intro-p2">
                                From a single classroom to a multi-location enterprise, we pair deep engineering with
                                hands-on support, so technology never becomes another thing to manage. We call it
                                crafting, because every deployment is shaped around the way you actually work. And we
                                sign off on every project the same way our clients close their goals:{" "}
                                <span className="font-serif italic text-ember">achieving all goals.</span>
                            </p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.3} className="relative">
                        <div className="absolute -inset-6 rounded-[2.5rem] bg-ember/10 blur-3xl" aria-hidden="true" />
                        <div className="relative overflow-hidden rounded-3xl border border-white/40 shadow-[0_40px_80px_-24px_rgba(26,25,24,0.35)]">
                            <img
                                src={ABOUT_IMAGE}
                                alt="Team collaborating around an interactive panel"
                                className="aspect-[4/3] w-full object-cover"
                                loading="eager"
                            />
                        </div>
                        <div
                            className="absolute -bottom-14 left-4 flex w-[250px] items-center gap-3 rounded-2xl border border-[#E2E8F4] bg-bone/95 p-3 shadow-xl backdrop-blur"
                            data-testid="about-logo-accent"
                        >
                            <img
                                src="/assets/logo-emblem-dark.png"
                                alt="Coaching Crafters AI emblem"
                                width={40}
                                height={40}
                                className="h-10 w-10 object-contain"
                            />
                            <span className="font-serif text-base font-semibold leading-tight text-ink">
                                Coaching Crafters <span className="text-ember">AI</span>
                            </span>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>

        {/* MISSION / VISION / GOALS */}
        <section className="grain relative bg-obsidian" data-testid="mvg-section">
            <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-28">
                <Reveal className="mx-auto max-w-3xl text-center">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember-soft">
                        What drives the craft
                    </p>
                    <h2 className="mt-4 font-serif text-3xl font-semibold text-[#F5F5F4] sm:text-4xl">
                        Mission, vision and the goals we sign off on
                    </h2>
                </Reveal>
                <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <Reveal>
                        <div
                            data-testid="mission-card"
                            className="h-full rounded-3xl border border-white/10 bg-obsidian-card p-8 transition-colors duration-300 hover:border-ember/50"
                        >
                            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ember/15">
                                <Target className="h-6 w-6 text-ember-soft" />
                            </span>
                            <h3 className="mt-6 font-serif text-2xl font-semibold text-[#F5F5F4]">Our Mission</h3>
                            <p className="mt-4 text-sm leading-relaxed text-[#F5F5F4]/65">
                                To make powerful technology simple, accessible and genuinely useful, giving
                                businesses, institutions and creators the tools they need to operate smarter and reach
                                every goal they set.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div
                            data-testid="vision-card"
                            className="h-full rounded-3xl border border-white/10 bg-obsidian-card p-8 transition-colors duration-300 hover:border-ember/50"
                        >
                            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ember/15">
                                <Eye className="h-6 w-6 text-ember-soft" />
                            </span>
                            <h3 className="mt-6 font-serif text-2xl font-semibold text-[#F5F5F4]">Our Vision</h3>
                            <p className="mt-4 text-sm leading-relaxed text-[#F5F5F4]/65">
                                To be the most trusted technology partner for growing organisations, known not just
                                for what we build, but for how far our clients go with it.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div
                            data-testid="goals-card"
                            className="h-full rounded-3xl border border-ember/40 bg-obsidian-card p-8"
                        >
                            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ember">
                                <ListChecks className="h-6 w-6 text-bone" />
                            </span>
                            <h3 className="mt-6 font-serif text-2xl font-semibold text-[#F5F5F4]">Our Goals</h3>
                            <ul className="mt-4 space-y-3">
                                {GOALS.map((g) => (
                                    <li key={g} className="flex items-start gap-3 text-sm leading-relaxed text-[#F5F5F4]/70">
                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-ember-soft" />
                                        {g}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10" data-testid="about-cta">
            <Reveal>
                <div className="flex flex-col items-center justify-between gap-8 rounded-3xl border border-[#E2E8F4] bg-white p-10 text-center shadow-sm lg:flex-row lg:text-left">
                    <div>
                        <h2 className="font-serif text-2xl font-semibold text-ink sm:text-3xl">
                            See what we craft
                        </h2>
                        <p className="mt-2 max-w-lg text-sm leading-relaxed text-ink/60">
                            Four product lines — AI web software, ERP, interactive panels and solar — one obsession
                            with craft.
                        </p>
                    </div>
                    <Link
                        to="/products"
                        data-testid="about-products-cta"
                        className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-ember px-7 py-3.5 text-sm font-semibold text-bone transition-colors hover:bg-ember-dark"
                    >
                        Explore our products
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                </div>
            </Reveal>
        </section>
    </>
);

export default About;
